/**
 * OnboardingSlideshowContainer.js
 *
 * Connected container that checks whether the current user needs
 * onboarding and manages the dismissed/completed state.
 *
 * Uses the same Redux state and user data patterns as the existing
 * StepsInViveka component for consistency.
 *
 * Usage:
 *   Import and render once, e.g. in app.js or a top-level wrapper:
 *
 *     import OnboardingSlideshowContainer from
 *       './custom-components/elements/onboarding-slideshow/OnboardingSlideshowContainer';
 *
 *     // Inside render:
 *     <OnboardingSlideshowContainer />
 */

import React, { Component } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import OnboardingSlideshow from './OnboardingSlideshow';

// Use the same localStorage key consistently
const STORAGE_KEY = 'viveka_onboarding_slideshow_seen';

class OnboardingSlideshowContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      dismissed: typeof window !== 'undefined'
        ? localStorage.getItem(STORAGE_KEY) === 'true'
        : false,
    };
    this.handleClose = this.handleClose.bind(this);
    this.handleGetStarted = this.handleGetStarted.bind(this);
  }

  handleClose() {
    this.setState({ dismissed: true });
    localStorage.setItem(STORAGE_KEY, 'true');
  }

  handleGetStarted() {
    this.setState({ dismissed: true });
    localStorage.setItem(STORAGE_KEY, 'true');

    // Navigate to profile settings — same route as StepsInViveka's
    // first "Go" button
    const { history } = this.props;
    setTimeout(() => {
      history.push('/profile-settings');
    }, 350);
  }

  render() {
    const { currentUser, isAuthenticated } = this.props;
    const { dismissed } = this.state;

    // Don't show if already dismissed
    if (dismissed) return null;

    // Must be logged in
    if (!isAuthenticated || !currentUser || !currentUser.id) return null;

    // Must be a provider (coach) — same check as StepsInViveka
    const isProvider =
      currentUser &&
      currentUser.attributes &&
      currentUser.attributes.profile &&
      currentUser.attributes.profile.publicData &&
      currentUser.attributes.profile.publicData.isProvider;

    if (!isProvider) return null;

    // Check how many onboarding steps are done — same fields as StepsInViveka
    const isProfilePicAdded = currentUser && currentUser.profileImage;
    const isStripeConnected = currentUser && currentUser.attributes && currentUser.attributes.stripeConnected;
    const privateData = (currentUser.attributes && currentUser.attributes.profile && currentUser.attributes.profile.privateData) || {};
    const isProgramCreated = privateData.isProgramCreated;
    const isAvailabilitySet = privateData.isAvailabilitySet;
    const isCreatedAListing = privateData.isCreatedAListing;

    // If ALL steps are already done, no need for slideshow
    if (isProfilePicAdded && isStripeConnected && isProgramCreated && isAvailabilitySet && isCreatedAListing) {
      return null;
    }

    return (
      <OnboardingSlideshow
        onClose={this.handleClose}
        onGetStarted={this.handleGetStarted}
      />
    );
  }
}

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const { isAuthenticated } = state.Auth;
  return { currentUser, isAuthenticated };
};

export default compose(
  connect(mapStateToProps),
  withRouter
)(OnboardingSlideshowContainer);
